import React, { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import { Accordion as AccordionPrimitive } from "radix-ui";
import { useTranslation } from "react-i18next";

import ProjectAPI from "@/src/api/ProjectAPI";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from "@/src/components/ui/accordion";
import { Badge } from "@/src/components/ui/badge";
import { Button } from "@/src/components/ui/button";
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/src/components/ui/dialog";
import { useCountryGroupMutations } from "@/src/hooks/mutations/useCountryGroupMutations";
import { getCountryLabelMap } from "@/src/utils/data/Countries";

import CountryGroupFormDialog from "./CountryGroupFormDialog";

import type { JSX } from "react";

import type { CountryGroup } from "@/src/api/ProjectAPI.types";

export default function GreographicGroupsConfiguration(): JSX.Element {
  const { t } = useTranslation("editor-organization");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CountryGroup | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<CountryGroup | null>(null);

  const { deleteCountryGroup, isDeleting } = useCountryGroupMutations();

  const getCountryGroupListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getCountryGroupList,
    params: [],
  });

  const { data, isLoading } = useQuery(getCountryGroupListQuery);

  const countryGroupList = (data?.data ?? []).toSorted(
    (firstGroup, secondGroup) =>
      Number(secondGroup.base_group) - Number(firstGroup.base_group),
  );

  const totalCountryCount = countryGroupList.reduce(
    (total, countryGroup) => total + countryGroup.countries.length,
    0,
  );

  const countryLabelMap = getCountryLabelMap();

  const handleCreate = (): void => {
    setEditingGroup(null);
    setIsFormOpen(true);
  };

  const handleEdit = (group: CountryGroup): void => {
    setEditingGroup(group);
    setIsFormOpen(true);
  };

  const handleDeleteConfirm = (): void => {
    if (!groupToDelete) return;
    deleteCountryGroup(groupToDelete.uuid, {
      onSettled: () => setGroupToDelete(null),
    });
  };

  function renderCountryGroup(countryGroup: CountryGroup): JSX.Element {
    const isEditable = !countryGroup.base_group;
    const countryLabelList = countryGroup.countries
      .map((country) => ({
        uuid: country.uuid,
        label: countryLabelMap[country.alpha2] ?? country.alpha2,
      }))
      .toSorted((firstCountry, secondCountry) =>
        firstCountry.label.localeCompare(secondCountry.label),
      );

    return (
      <AccordionItem
        key={countryGroup.uuid}
        className="border-border px-6.25"
        value={countryGroup.uuid}
      >
        <AccordionPrimitive.Header className="flex items-center">
          <AccordionPrimitive.Trigger className="group flex flex-1 items-center gap-2.5 py-4 text-left outline-none">
            <ChevronRight className="text-muted-foreground size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90" />
            <span className="font-medium">{countryGroup.label}</span>
            <span className="text-muted-foreground text-sm font-normal">
              {t("geographicGroups.countryCount", {
                count: countryGroup.countries.length,
              })}
            </span>
          </AccordionPrimitive.Trigger>
          {isEditable ? (
            <div className="flex shrink-0 items-center gap-1 pl-2">
              <Button
                aria-label={t("geographicGroups.editGroup")}
                onClick={(): void => handleEdit(countryGroup)}
                size="icon"
                variant="ghost"
              >
                <Pencil />
              </Button>
              <Button
                aria-label={t("geographicGroups.deleteGroup")}
                onClick={(): void => setGroupToDelete(countryGroup)}
                size="icon"
                variant="ghost"
              >
                <Trash2 />
              </Button>
            </div>
          ) : (
            <Badge className="text-muted-foreground shrink-0" variant="outline">
              {t("geographicGroups.predefined")}
            </Badge>
          )}
        </AccordionPrimitive.Header>
        <AccordionContent className="pl-6.5">
          {countryLabelList.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {countryLabelList.map((country) => (
                <Badge
                  key={country.uuid}
                  className="rounded-md px-2.5 py-1 text-sm font-normal"
                  variant="secondary"
                >
                  {country.label}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">
              {t("geographicGroups.noCountry")}
            </span>
          )}
        </AccordionContent>
      </AccordionItem>
    );
  }

  function renderContent(): JSX.Element {
    if (isLoading) {
      return (
        <p className="text-muted-foreground px-6.25 py-4 text-sm">
          {t("geographicGroups.loading")}
        </p>
      );
    }

    if (countryGroupList.length === 0) {
      return (
        <p className="text-muted-foreground px-6.25 py-4 text-sm">
          {t("geographicGroups.empty")}
        </p>
      );
    }

    return (
      <Accordion type="multiple">
        {countryGroupList.map((countryGroup) =>
          renderCountryGroup(countryGroup),
        )}
      </Accordion>
    );
  }

  return (
    <Card className="mt-3.5 gap-0 py-0">
      <CardHeader className="border-b py-4 [.border-b]:pb-4">
        <CardTitle>{t("geographicGroups.title")}</CardTitle>
        <CardDescription>
          {t("geographicGroups.groupCount", {
            count: countryGroupList.length,
          })}
          {" · "}
          {t("geographicGroups.countryCount", { count: totalCountryCount })}
        </CardDescription>
        <CardAction>
          <Button onClick={handleCreate}>
            <Plus />
            {t("geographicGroups.addGroup")}
          </Button>
        </CardAction>
      </CardHeader>
      {renderContent()}

      <CountryGroupFormDialog
        group={editingGroup}
        onClose={(): void => setIsFormOpen(false)}
        open={isFormOpen}
      />

      <Dialog
        open={groupToDelete !== null}
        onOpenChange={(next: boolean): void => {
          if (!next) setGroupToDelete(null);
        }}
      >
        <DialogContent className="bg-card sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("geographicGroups.deleteGroup")}</DialogTitle>
            <DialogDescription>
              {t("geographicGroups.deleteConfirm", {
                name: groupToDelete?.label,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={(): void => setGroupToDelete(null)}
              type="button"
              variant="outline"
            >
              {t("editor-offers:cancel")}
            </Button>
            <Button
              disabled={isDeleting}
              onClick={handleDeleteConfirm}
              type="button"
              variant="destructive"
            >
              {t("geographicGroups.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
